import { createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import {
  PrimeModelCreationData,
  PrimeArtifactState,
  PrimeProcess,
  PrimeArtifactStateTransitionCreate,
  PrimeArtifactStateTransition,
} from '../../types/primeModel';
import { PrimeArtifactStateTransitionUpdate } from '../../types/primeArtifactStateTransition';
import { PrimeArtifact, UpdatePrimeArtifact } from '../../types/primeArtifact';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { API_ENDPOINTS, buildApiUrl } from '../../services/api/config';

// Fetch prime artifacts hierarchy
export const fetchPrimeArtifactsHierarchy = createAsyncThunk(
  'primeArtifacts/fetchHierarchy',
  async () => {
    const response = await api.get<PrimeArtifact[]>(
      buildApiUrl(API_ENDPOINTS.PRIME_ARTIFACTS.HIERARCHY)
    );
    return response.data;
  }
);

// Fetch flat list of artifacts
export const fetchFlatArtifacts = createAsyncThunk(
  'primeArtifacts/fetchFlatArtifacts',
  async () => {
    const allArtifacts: PrimeArtifact[] = [];
    let skip = 0;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const response = await api.get<PrimeArtifact[]>(
        buildApiUrl(`${API_ENDPOINTS.PRIME_ARTIFACTS.BASE}?skip=${skip}&limit=${limit}`)
      );
      const artifacts = response.data;
      allArtifacts.push(...artifacts);

      // If we got fewer artifacts than the limit, we've reached the end
      if (artifacts.length < limit) {
        hasMore = false;
      } else {
        skip += limit;
      }
    }

    return allArtifacts;
  }
);

// Fetch prime artifacts
export const fetchPrimeArtifacts = createAsyncThunk('primeArtifacts/fetchAll', async () => {
  const response = await api.get<PrimeArtifact[]>(API_ENDPOINTS.PRIME_ARTIFACTS.BASE);
  return response.data;
});

// Fetch prime artifact states
export const fetchPrimeArtifactStates = createAsyncThunk(
  'primeArtifacts/fetchStates',
  async (artifactId: string) => {
    console.log('[fetchPrimeArtifactStates] Fetching states for artifact:', artifactId);
    const response = await api.get<PrimeArtifactState[]>(
      API_ENDPOINTS.PRIME_ARTIFACTS.STATES(artifactId)
    );
    console.log('[fetchPrimeArtifactStates] Raw API Response:', response.data);
    return response.data;
  }
);

// Fetch prime artifact transitions
export const fetchPrimeArtifactTransitions = createAsyncThunk(
  'primeArtifacts/fetchTransitions',
  async (artifactId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<PrimeArtifactStateTransition[]>(
        API_ENDPOINTS.PRIME_ARTIFACTS.TRANSITIONS(artifactId)
      );
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: unknown } }).response?.data || 'Failed to fetch transitions'
      );
    }
  }
);

// Fetch prime processes
export const fetchPrimeProcesses = createAsyncThunk(
  'primeArtifacts/fetchProcesses',
  async (artifactId: string) => {
    const response = await api.get<PrimeProcess[]>(
      buildApiUrl(API_ENDPOINTS.PRIME_PROCESSES.BY_ARTIFACT(artifactId))
    );
    return response.data;
  }
);

// Create prime model
export const createPrimeModel = createAsyncThunk(
  'primeArtifacts/createModel',
  async (modelData: PrimeModelCreationData) => {
    const response = await api.post(buildApiUrl('/prime-models'), modelData);
    return response.data;
  }
);

// Update prime model
export const updatePrimeModel = createAsyncThunk(
  'primeArtifacts/updateModel',
  async ({ modelId, modelData }: { modelId: string; modelData: PrimeModelCreationData }) => {
    const response = await api.put(buildApiUrl(`/prime-models/${modelId}`), modelData);
    return response.data;
  }
);

// Delete prime model
export const deletePrimeModel = createAsyncThunk(
  'primeArtifacts/deleteModel',
  async (modelId: string) => {
    await api.delete(buildApiUrl(`/prime-models/${modelId}`));
    return modelId;
  }
);

// Update prime artifact
export const updatePrimeArtifact = createAsyncThunk(
  'primeArtifacts/updateArtifact',
  async (artifact: UpdatePrimeArtifact) => {
    // Only send fields that the backend expects
    // Explicitly include null for parent_ref to allow clearing it
    const updateData: any = {
      name: artifact.name,
      description: artifact.description,
      artifact_type_id: artifact.artifact_type_id,
      parent_ref: artifact.parent_ref === undefined ? null : artifact.parent_ref,
    };

    const response = await api.put(
      buildApiUrl(API_ENDPOINTS.PRIME_ARTIFACTS.UPDATE_ARTIFACT(artifact.id)),
      updateData
    );
    return response.data;
  }
);

// Fetch artifact types
export const fetchArtifactTypes = createAsyncThunk(
  'primeArtifacts/fetchArtifactTypes',
  async () => {
    const response = await api.get<{ id: string; name: string }[]>(
      buildApiUrl(API_ENDPOINTS.ARTIFACT_TYPES.BASE)
    );
    return response.data;
  }
);

// Create prime artifact state
export const createPrimeArtifactState = createAsyncThunk(
  'primeArtifacts/createState',
  async (state: Omit<PrimeArtifactState, 'id'>) => {
    const response = await api.post(buildApiUrl(API_ENDPOINTS.PRIME_ARTIFACT_STATES.BASE), state);
    return response.data;
  }
);

// Create prime artifact transition
export const createPrimeArtifactTransition = createAsyncThunk(
  'primeArtifact/createTransition',
  async ({
    artifact_id,
    transition,
  }: {
    artifact_id: string;
    transition: PrimeArtifactStateTransitionCreate;
  }) => {
    const response = await PrimeArtifactService.createPrimeArtifactTransition(
      artifact_id,
      transition
    );
    return response;
  }
);

// Update prime artifact transition
export const updatePrimeArtifactTransition = createAsyncThunk(
  'primeArtifacts/updateTransition',
  async ({
    artifact_id,
    transition_id,
    transition,
  }: {
    artifact_id: string;
    transition_id: string;
    transition: PrimeArtifactStateTransitionUpdate;
  }) => {
    const response = await PrimeArtifactService.updatePrimeArtifactTransition(
      artifact_id,
      transition_id,
      transition
    );
    return response;
  }
);

// Delete prime artifact transition
export const deletePrimeArtifactTransition = createAsyncThunk(
  'primeArtifacts/deleteTransition',
  async ({ artifactId, transitionId }: { artifactId: string; transitionId: string }) => {
    await api.delete(
      buildApiUrl(API_ENDPOINTS.PRIME_ARTIFACTS.DELETE_TRANSITION(artifactId, transitionId))
    );
    return transitionId;
  }
);

// Create prime process
export const createPrimeProcess = createAsyncThunk(
  'primeArtifacts/createProcess',
  async (process: Omit<PrimeProcess, 'id'>) => {
    const response = await api.post(buildApiUrl(API_ENDPOINTS.PRIME_PROCESSES.BASE), process);
    return response.data;
  }
);

// Update prime process
export const updatePrimeProcess = createAsyncThunk(
  'primeArtifacts/updateProcess',
  async (process: Omit<PrimeProcess, 'id'>) => {
    const response = await api.put(buildApiUrl(API_ENDPOINTS.PRIME_PROCESSES.BASE), process);
    return response.data;
  }
);

// Delete prime process
export const deletePrimeProcess = createAsyncThunk(
  'primeArtifacts/deleteProcess',
  async (processId: string) => {
    await api.delete(buildApiUrl(API_ENDPOINTS.PRIME_PROCESSES.BY_ID(processId)));
    return processId;
  }
);

// Delete prime artifact
export const deletePrimeArtifact = createAsyncThunk(
  'primeArtifacts/deleteArtifact',
  async (artifactId: string) => {
    await api.delete(buildApiUrl(API_ENDPOINTS.PRIME_ARTIFACTS.BY_ID(artifactId)));
    return artifactId;
  }
);
